import { CommonModule } from '@angular/common';
import {
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  Input,
  OnInit,
} from '@angular/core';
import { EventRSVPConfirmationPageSection } from '@models/events/event-rsvp-confirmation-page-section.model';
import { Event } from '@models/events/event.model';
import { RSVPConfirmationPageSectionIconsColorType } from '@models/events/rsvp-confirmation-page-section-icons-color-type.enum';
import { SocialMediaType } from '@models/social-media/enums/social-media-type.enum';
import { SocialMediaLink } from '@models/social-media/social-media-link.model';
import { SocialMedia } from '@models/social-media/social-media.model';
import { TranslateService } from '@ngx-translate/core';
import { NotificationService } from '@services/shared/notification.service';
import { Clipboard } from '@angular/cdk/clipboard';
import { Helpers } from '@utils/helpers';
import { SwiperOptions } from 'swiper/types';
import { SwiperDirective } from '../../../../directives/swiper.directive';
import { ScreenWidthService } from '@services/shared/screen-width.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { SwiperContainer } from 'swiper/element';
@Component({
  selector: 'app-edit-social-media-buttons',
  standalone: true,
  imports: [CommonModule, SwiperDirective],
  templateUrl: './edit-social-media-buttons.component.html',
  styleUrl: './edit-social-media-buttons.component.scss',
  providers: [NotificationService, ScreenWidthService],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class EditSocialMediaButtonsComponent implements OnInit {
  @Input() section?: EventRSVPConfirmationPageSection;
  @Input() asSvg: boolean = false;
  @Input() biggerSize: boolean = false;
  socialMediaLinks: SocialMediaLink[] = [];
  socialMediaTypes = SocialMediaType.getAll();
  iconsColor?: RSVPConfirmationPageSectionIconsColorType;
  swiperConfig: SwiperOptions = {
    slidesPerView: 'auto',
    speed: 2000,
    spaceBetween: 0,
    injectStyles: [],
  };

  private _event?: Event;
  private _isEditMode: boolean = true;

  private _alignEnd: boolean = false;
  private _alignCenter: boolean = false;

  @Input()
  set alignEnd(value: boolean) {
    this._alignEnd = value;
    this.updateSwiperStyles();
  }
  get alignEnd(): boolean {
    return this._alignEnd;
  }

  @Input() set isEditMode(value: boolean | undefined) {
    if (value !== undefined) this._isEditMode = value;
    this.updateSwiperStyles();
  }

  get isEditMode(): boolean {
    return this._isEditMode;
  }
  private _socialMedia?: SocialMedia;

  @Input()
  set socialMedia(value: SocialMedia | undefined) {
    this._socialMedia = value;
    if (this._socialMedia) {
      this.socialMediaLinks = this._socialMedia.links;
    }
    this.updateSwiperStyles();
  }

  get socialMedia(): SocialMedia | undefined {
    return this._socialMedia;
  }
  @Input()
  set event(value: Event | undefined) {
    this._event = value;

    if (this._event) {
      if (
        this._event &&
        this._event.eventTemplate &&
        this._event.eventTemplate.socialMedia
      ) {
        this.socialMediaLinks = this._event.eventTemplate.socialMedia.links;
      }
    }
  }

  get event(): Event | undefined {
    return this._event;
  }

  isOver600 = false;
  swiperVisible = true;

  constructor(
    private notificationService: NotificationService,
    private translateService: TranslateService,
    private clipboard: Clipboard,
    private screenWidthService: ScreenWidthService,
  ) {
    this.screenWidthService
      .isOver600()
      .pipe(takeUntilDestroyed())
      .subscribe((isOver600) => {
        this.isOver600 = isOver600;
      });
  }

  ngOnInit(): void {
    if (
      !this.event ||
      !this.event.eventTemplate ||
      !this.event.eventTemplate.socialMedia
    ) {
      return;
    }

    if (this.event) {
      this.socialMediaLinks = this.event.eventTemplate.socialMedia.links;
    }
  }

  private updateSwiperStyles() {
    const baseStyles = `
      :host .swiper { overflow: visible;
        ${this.alignEnd ? 'display: flex; justify-content: end;' : ''}
      }
      :host .swiper-wrapper {
          margin: 0;
          justify-content: center;
          ${this.alignEnd ? 'width: auto;' : ''};
        }
      :host .slider-active .swiper-wrapper { justify-content: normal; }
  `;

    this.swiperConfig.injectStyles = [baseStyles];
    this.swiperVisible = false;
    setTimeout(() => {
      this.swiperVisible = true;
      setTimeout(() => {
        this.setSliderActiveClass(
          '.generic-after-attendee-card .social-media-swiper-container',
        );
      });
    }, 10);
  }

  missingLink(type: SocialMediaType): boolean {
    return !this.socialMediaLinks.some(
      (link) => link.type === type && link.link.trim() !== '',
    );
  }

  getThemeIconColors(): string {
    if (this.iconsColor === RSVPConfirmationPageSectionIconsColorType.THEME) {
      const color = document.documentElement.style.getPropertyValue(
        '--event-theme-primary-color',
      );
      return Helpers.getTextColorForBg(color);
    } else return '';
  }

  copyLink(type: SocialMediaType) {
    if (!this.isEditMode) {
      let linkItem = this.socialMediaLinks.find((x) => x.type === type);
      if (linkItem) {
        if (Helpers.isValidUrl(linkItem.link)) {
          window.open(linkItem.link, '_blank');
        } else {
          this.clipboard.copy(linkItem.link);
          this.notificationService.successWithClass(
            this.translateService.instant(
              'APP.AFFILIATE_PRODUCTS_PAGE_LIST.SUCCESS.LINK_COPIED',
            ),
            'toast-success-white-event-primary',
          );
        }
      }
    }
  }

  setSliderActiveClass(selector: string) {
    const swiperContainer: SwiperContainer | null =
      document.querySelector(selector);

    if (swiperContainer) {
      const swiperEl = swiperContainer.shadowRoot?.querySelector('.swiper');
      const swiperInstance: any = swiperContainer.swiper;

      if (swiperEl && swiperInstance) {
        const applySliderActiveClass = () => {
          if (this.socialMediaLinks) {
            const numberSocialMediaLinks = this.socialMediaTypes.filter(
              (x) => !this.missingLink(x),
            ).length;

            if (
              swiperInstance.size >
              numberSocialMediaLinks * this.getSwiperElementFullWidth()
            ) {
              swiperEl.classList.remove('slider-active');
            } else {
              swiperEl.classList.add('slider-active');
            }
          }
        };

        swiperInstance.on('resize', applySliderActiveClass);
        applySliderActiveClass();
      }
    }
  }

  getSwiperElementFullWidth(): number {
    if (this.isEditMode) {
      return 56;
    } else if (this.biggerSize) {
      return 40;
    }

    if (this.isOver600) {
      return 32;
    }
    return 40;
  }

  protected readonly SocialMediaType = SocialMediaType;
  protected readonly RSVPConfirmationPageSectionIconsColorType =
    RSVPConfirmationPageSectionIconsColorType;
}
